import { BsAward, BsBuilding, BsCardChecklist, BsCardList, BsFileEarmark, BsStar, BsViewStacked } from 'react-icons/bs';
import moment from 'moment-timezone';

import { FeatureFlags } from '../../../../components/Authorizer';
import { CodeStatuses } from '../../../../constants/codes';
import CompanySettings from '../../../../constants/companySettings';
import * as Entities from '../../../../constants/entities';
import { PolicyTypesSelectOptions } from '../../../../constants/policyTypes';
import { Statuses } from '../../../../constants/statuses';
import { filterOperators, filterTypes } from '../../../../redux/blueprints/search';
import { getStatusFilterSelectOptions } from '../../../../utils/statusUtil';

const advancedFilters = [
  {
    name: 'insureds',
    icon: BsBuilding,
    subProperties: [
      {
        entity: 'insured',
        name: 'name / company name',
        key: 'insured.company_name',
        type: filterTypes.string,
        operators: [
          filterOperators.isEqualTo,
          filterOperators.startsWith
        ]
      },
      {
        entity: 'insured',
        name: 'overall status',
        key: 'insured.overall_status',
        type: filterTypes.enum,
        operators: [
          filterOperators.isEqualTo
        ],
        values: getStatusFilterSelectOptions(Entities.Insured)?.filter(s => s.value !== Statuses.Archived)
      },
      {
        entity: 'insured',
        name: 'parent name',
        key: 'parent_insured.company_name',
        type: filterTypes.string,
        operators: [
          filterOperators.isEqualTo,
          filterOperators.startsWith
        ],
        setting: CompanySettings.ReportsHideNonPolicyHolders
      },
      {
        entity: 'insured',
        name: 'parent number',
        key: 'parent_insured.external_id',
        type: filterTypes.string,
        operators: [
          filterOperators.isEqualTo,
          filterOperators.startsWith
        ],
        setting: CompanySettings.ReportsHideNonPolicyHolders
      },
      {
        entity: 'insured',
        name: 'guarantor name',
        key: 'insured.guarantor_name',
        type: filterTypes.string,
        operators: [
          filterOperators.isEqualTo,
          filterOperators.startsWith
        ]
      },
      {
        entity: 'insured',
        name: 'legal name',
        key: 'insured.legal_name',
        type: filterTypes.string,
        operators: [
          filterOperators.isEqualTo,
          filterOperators.startsWith
        ]
      },
      {
        entity: 'insured',
        name: 'last email date',
        key: 'insured.latest_email.created_at',
        type: filterTypes.date,
        operators: [
          filterOperators.isAfter,
          filterOperators.isBefore
        ]
      }
    ]
  },
  {
    name: 'documents',
    icon: BsFileEarmark,
    featureFlag: FeatureFlags.SearchByDocuments,
    subProperties: [
      {
        entity: 'document',
        name: 'requirement type',
        key: 'insurance_documents.type.id',
        type: filterTypes.enum,
        operators: [
          filterOperators.isEqualTo
        ],
        labelKey: 'displayName',
        idKey: 'id'
      },
      {
        entity: 'document',
        name: 'file name',
        key: 'insurance_documents.original_name',
        type: filterTypes.string,
        operators: [
          filterOperators.startsWith,
          filterOperators.contains,
          filterOperators.endsWith
        ]
      },
      {
        entity: 'document',
        name: 'upload date',
        key: 'insurance_documents.updated_at',
        type: filterTypes.date,
        operators: [
          filterOperators.isAfter,
          filterOperators.isBefore
        ]
      },
      {
        entity: 'document',
        name: 'expires on',
        key: 'insurance_documents.expires_at',
        type: filterTypes.date,
        operators: [
          filterOperators.isAfter,
          filterOperators.isBefore
        ]
      }
    ]
  },
  {
    name: 'units',
    icon: BsViewStacked,
    featureFlag: FeatureFlags.Items,
    subProperties: [
      {
        entity: 'unit',
        name: 'unit number',
        key: 'item.property_values.unit_number',
        type: filterTypes.string,
        operators: [
          filterOperators.isEqualTo,
          filterOperators.startsWith
        ]
      },
      {
        entity: 'unit',
        name: 'quote number',
        key: 'item.property_values.quote_number',
        type: filterTypes.string,
        operators: [
          filterOperators.isEqualTo,
          filterOperators.contains
        ]
      },
      {
        entity: 'unit',
        name: 'vin number',
        key: 'item.property_values.vin_nbr',
        type: filterTypes.string,
        operators: [
          filterOperators.isEqualTo,
          filterOperators.startsWith,
          filterOperators.endsWith,
          filterOperators.contains
        ]
      },
      {
        entity: 'unit',
        name: 'effective on',
        key: 'insurance_policies.effective_at',
        type: filterTypes.date,
        operators: [
          filterOperators.isAfter,
          filterOperators.isBefore
        ]
      }
    ]
  },
  {
    name: 'codes',
    icon: BsStar,
    featureFlag: FeatureFlags.Codes,
    subProperties: [
      {
        entity: 'code',
        name: 'letter',
        key: 'item.codes.letter',
        nestedPath: 'item.codes',
        type: filterTypes.enum,
        operators: [
          filterOperators.isEqualTo
        ],
        values: []
      },
      {
        entity: 'code',
        name: 'status',
        key: 'item.codes.status',
        nestedPath: 'item.codes',
        type: filterTypes.enum,
        operators: [
          filterOperators.isEqualTo
        ],
        values: [
          { value: CodeStatuses.Applied, text: CodeStatuses.Applied },
          { value: CodeStatuses.Suggested, text: CodeStatuses.Suggested },
          { value: CodeStatuses.Waived, text: CodeStatuses.Waived },
          { value: CodeStatuses.Inquiring, text: CodeStatuses.Inquiring },
          { value: CodeStatuses.RemovalSuggested, text: 'removal suggested' }
        ]
      },
      {
        entity: 'code',
        name: 'applied on',
        key: 'item.codes.created_at',
        nestedPath: 'item.codes',
        type: filterTypes.date,
        operators: [
          filterOperators.isAfter,
          filterOperators.isBefore
        ]
      }
    ]
  },
  {
    name: 'projects',
    icon: BsCardList,
    subProperties: [
      {
        entity: 'project',
        name: 'name',
        key: 'job.name',
        type: filterTypes.string,
        operators: [
          filterOperators.isEqualTo,
          filterOperators.startsWith
        ]
      },
      {
        entity: 'project',
        name: 'number',
        key: 'job.number',
        type: filterTypes.string,
        operators: [
          filterOperators.isEqualTo,
          filterOperators.startsWith
        ]
      },
      {
        entity: 'project',
        name: 'owner',
        key: 'job.owner.id',
        type: filterTypes.enum,
        operators: [
          filterOperators.isEqualTo
        ],
        values: []
      },
      {
        entity: 'project',
        name: 'Record Number',
        key: 'insurance_requirement.contract_number',
        type: filterTypes.string,
        operators: [
          filterOperators.isEqualTo,
          filterOperators.startsWith
        ]
      }
    ]
  },
  {
    name: 'policies',
    icon: BsAward,
    subProperties: [
      {
        entity: 'policy',
        name: 'expires on',
        key: 'insurance_policies.expires_at',
        type: filterTypes.date,
        operators: [
          filterOperators.isAfter,
          filterOperators.isBefore
        ]
      },
      {
        entity: 'policy',
        name: 'broker email',
        key: 'insurance_policies.owner.email',
        type: filterTypes.string,
        operators: [
          filterOperators.isEqualTo,
          filterOperators.startsWith
        ]
      },
      {
        entity: 'policy',
        name: 'effective on',
        key: 'insurance_policies.effective_at',
        type: filterTypes.date,
        operators: [
          filterOperators.isAfter,
          filterOperators.isBefore
        ]
      }
    ]
  },
  {
    name: 'requirements',
    icon: BsCardChecklist,
    subProperties: [
      {
        entity: 'requirement',
        name: 'type',
        key: 'insurance_requirement.properties.policy_type.name',
        type: filterTypes.enum,
        operators: [
          filterOperators.isEqualTo
        ],
        values: PolicyTypesSelectOptions
      },
      {
        entity: 'requirement',
        name: 'status',
        keys: [
          'insurance_requirement.properties.status',
          'insurance_requirement.documents.status'
        ],
        type: filterTypes.enum,
        shouldQuery: true,
        operators: [
          filterOperators.isEqualTo
        ],
        values: getStatusFilterSelectOptions(Entities.InsuranceRequirement)?.filter(s => !([Statuses.Archived, Statuses.Deleted]).includes(s.value)),
        accompanyingFilters: [
          {
            key: 'insurance_policies.expires_at',
            type: 'date',
            operator: filterOperators.isAfter,
            value: moment.utc().toISOString()
          }
        ]
      }
    ]
  }

];

export default advancedFilters;
